
import { Code, PenTool, BarChart3, Users, Zap, Briefcase, GraduationCap, TrendingUp, Award } from 'lucide-react';

const industries = [
    { name: 'FinTech', desc: 'Transforming financial services', baseSalary: 85000 },
    { name: 'HealthTech', desc: 'Improving patient outcomes', baseSalary: 80000 },
    { name: 'EdTech', desc: 'Revolutionizing learning', baseSalary: 75000 },
    { name: 'GreenTech', desc: 'Building sustainable futures', baseSalary: 78000 },
    { name: 'Media', desc: 'Creating global entertainment', baseSalary: 72000 },
    { name: 'CyberSecurity', desc: 'Protecting digital assets', baseSalary: 95000 },
    { name: 'E-Commerce', desc: 'Connecting global markets', baseSalary: 82000 },
    { name: 'SaaS', desc: 'Powering enterprise efficiency', baseSalary: 88000 }
];

/**
 * Maps common role keywords to distinct adjacent career paths.
 * Returns tailored options with rich descriptions.
 */
const ROLE_ARCHETYPES = {
    'Data': {
        keywords: ['data', 'scientist', 'analyst', 'machine learning', 'ai', 'statistics', 'nlp'],
        options: [
            { title: 'Junior Software Engineer', type: 'Engineering', template: "Shift your focus to building the scalable systems that power data products." },
            { title: 'Associate Product Manager', type: 'Product', template: "Leverage your analytical background to drive data-informed product strategy." },
            { title: 'Junior Quant Researcher', type: 'Finance', template: "Apply your modeling skills to solve complex financial challenges." },
            { title: 'Junior Analytics Engineer', type: 'Data', template: "Bridge the gap between data science and data infrastructure." }
        ]
    },
    'Engineering': {
        keywords: ['engineer', 'developer', 'stack', 'programmer', 'software', 'architect', 'web'],
        options: [
            { title: 'Associate Product Manager', type: 'Product', template: "Transition from building features to defining the roadmap and 'why'." },
            { title: 'Junior Data Engineer', type: 'Data', template: "Specialize in designing the pipelines and architecture for big data." },
            { title: 'Solutions Analyst', type: 'Consulting', template: "Design high-level technical strategies for diverse enterprise clients." },
            { title: 'Junior DevOps Engineer', type: 'Infrastructure', template: "Focus on the tools, automation, and reliability of software delivery." }
        ]
    },
    'Product': {
        keywords: ['product', 'manager', 'owner', 'scrum', 'agile'],
        options: [
            { title: 'Associate PMM', type: 'Marketing', template: "Focus on the positioning, messaging, and go-to-market strategy." },
            { title: 'Junior User Researcher', type: 'Design', template: "Deep dive into user needs to inform product direction." },
            { title: 'Junior Data Analyst', type: 'Data', template: "Use data to uncover insights that drive product decisions." },
            { title: 'Project Coordinator', type: 'Management', template: "Orchestrate complex cross-functional technical initiatives." }
        ]
    },
    'Design': {
        keywords: ['design', 'ux', 'ui', 'creative', 'art', 'graphic', 'visual'],
        options: [
            { title: 'Junior Frontend Developer', type: 'Engineering', template: "Bridge design and code by implementing pixel-perfect interfaces." },
            { title: 'Associate Product Manager', type: 'Product', template: "Lead the product vision while advocating for user experience." },
            { title: 'Brand Coordinator', type: 'Marketing', template: "Shape the visual and narrative identity of the brand." },
            { title: 'Junior UX Researcher', type: 'Research', template: "Conduct research to validate design decisions and understand users." }
        ]
    },
    'Marketing': {
        keywords: ['marketing', 'brand', 'content', 'social', 'media', 'growth', 'seo'],
        options: [
            { title: 'Sales Ops Analyst', type: 'Sales', template: "Optimize the processes and tools that drive sales efficiency." },
            { title: 'Associate Product Manager', type: 'Product', template: "Define the product roadmap with a deep understanding of the market." },
            { title: 'Customer Success Specialist', type: 'Support', template: "Ensure improved client retention and satisfaction post-sale." },
            { title: 'Digital Strategy Analyst', type: 'Consulting', template: "Advise clients on digital transformation and growth strategies." }
        ]
    }
};

/**
 * Generates 8 distinct "Starting Point" options.
 * Combinatorial generation of Role x Industry to provide specific contexts.
 * Prioritizes userContext interests if available.
 */
export const generateInitialOptions = (seedNode, userContext) => {
    let title = seedNode.title || "Entry Level Role";
    
    // --- SENIORITY LOGIC ---
    // Check user context for experience level
    const currentYear = new Date().getFullYear();
    
    // Robust extraction of graduation date
    const gradDate = userContext?.education?.graduation_date || 
                     userContext?.graduation_date || 
                     userContext?.resume_data?.education?.graduation_date ||
                     userContext?.resume_data?.graduation_date;
                     
    const gradYear = gradDate ? new Date(gradDate).getFullYear() : null;
    
    // Robust extraction of experience
    // Calculate from resume roles if necessary, or use explicit fields
    let yearsExp = userContext?.years_of_experience || userContext?.experience_years;
    if (yearsExp === undefined && userContext?.resume_data?.years_experience) {
        yearsExp = userContext.resume_data.years_experience;
    }
    
    // Determine if user is effectively a student/entry-level
    // 1. Explicit Student: Graduation >= Current Year
    const isStudent = gradYear && gradYear >= currentYear;
    
    // 2. Explicit Entry Level: Experience known and < 2 years
    const isJuniorByExp = (yearsExp !== undefined && yearsExp !== null && yearsExp < 2);
    
    // Default to strict entry level ONLY if we have evidence (Student or Junior Exp)
    // If we know nothing, we shouldn't indiscriminately downgrade "Senior Manager"
    const isEntryLevel = isStudent || isJuniorByExp;

    if (isEntryLevel) {
        // --- FORCE ENTRY LEVEL TITLES (Original Logic) ---
        // Strip seniority
        title = title.replace(/Senior |Staff |Principal |Lead |Chief |Head of |Director of |VP of /gi, "").trim();
        
        // Add appropriate entry-level prefixes if not present
        const isAlreadyEntry = /Junior|Associate|Entry|Intern|Trainee|Analyst|Coordinator/i.test(title);
        
        if (!isAlreadyEntry) {
            if (title.includes("Product Manager")) {
                title = "Associate Product Manager";
            } else if (title.includes("Consultant")) {
                title = "Associate Consultant";
            } else if (title.includes("Sales")) {
                title = "Sales Development Rep";
            } else if (title.includes("Marketing")) {
                title = "Marketing Coordinator";
            } else if (title.includes("Resources") || title.includes("HR")) {
                title = "HR Coordinator";
            } else {
                // Default catch-all
                title = "Junior " + title;
            }
        }
    } else {
        // --- PRESERVE/ENFORCE SENIORITY FOR EXPERIENCED USERS ---
        // If the seed role is inadvertantly "Junior", strip it
        title = title.replace(/Junior |Entry |Intern |Trainee /gi, "").trim();
        
        // Optionally, if they are VERY experienced (e.g. 5+ years), ensure "Senior" is present?
        // But the seed node usually comes from a good recommendation. We just ensure we don't downgrade `Senior Software Engineer`.
    }

    // Detect archetype
    let archetype = 'Engineering'; // Default
    const lowerTitle = title.toLowerCase();
    
    for (const [key, data] of Object.entries(ROLE_ARCHETYPES)) {
        if (data.keywords.some(k => lowerTitle.includes(k))) {
            archetype = key;
            break;
        }
    }
    
    // Sort industries: if userContext has interests, buble match to top
    // Pseudo-logic: simple rotation or shuffle if no context
    // We can assume userContext.interests is a string or array
    let outputIndustries = [...industries];
    if (userContext?.interests) {
        // Simple heuristic sort
        const interestStr = Array.isArray(userContext.interests) ? userContext.interests.join(' ').toLowerCase() : userContext.interests.toLowerCase();
        outputIndustries.sort((a, b) => {
            const aMatch = interestStr.includes(a.name.toLowerCase()) || interestStr.includes(a.desc.toLowerCase());
            const bMatch = interestStr.includes(b.name.toLowerCase()) || interestStr.includes(b.desc.toLowerCase());
            return bMatch - aMatch;
        });
    }

    // Get distinct role types (including the original)
    // DIVERSIFICATION: Ensure we don't duplicate role titles
    const distinctRoles = [
        { title: title, type: 'Recommended', template: "The recommended starting point optimally aligned with your profile." },
        ...(ROLE_ARCHETYPES[archetype]?.options || ROLE_ARCHETYPES['Engineering'].options)
    ];
    
    // Deduplicate roles by title
    const uniqueTitles = new Set();
    const uniqueDistinctRoles = [];
    for (const r of distinctRoles) {
        if (!uniqueTitles.has(r.title)) {
            uniqueTitles.add(r.title);
            uniqueDistinctRoles.push(r);
        }
    }

    // Helper to build a node
    const createOption = (roleTitle, industryObj, type, templateDesc) => {
        const specificTitle = `${industryObj.name} ${roleTitle}`;
        // Enrich description with industry context
        const description = templateDesc 
            ? `${templateDesc} (${industryObj.desc})`
            : `Apply your skills as a ${roleTitle} to the challenge of ${industryObj.desc.toLowerCase()}.`;

        // Generate plausible salary range
        const base = industryObj.baseSalary || 90000;
        const variance = Math.floor(Math.random() * 15000);
        const min = (base - variance) / 1000;
        const max = (base + variance + 20000) / 1000;
        const salaryRange = `$${Math.floor(min)}k - $${Math.floor(max)}k`;

        return {
            ...seedNode,
            role_id: `${seedNode.role_id}_start_${Date.now()}_${Math.random()}`,
            title: specificTitle,
            year: new Date().getFullYear(),
            description: description,
            salary: salaryRange,
            company: industryObj.name + " Systems",
            type: 'role', // Ensure consistent type
            details: {
                skills: userContext?.skills || ['Fundamentals', 'Core Competencies', industryObj.name],
                actions: ['Establish foundation', 'Learn core tools', 'Build industry knowledge']
            }
        };
    };
    
    const options = [];
    const usedIndustryNames = new Set();
    
    // Generate 1 distinct option per Role Type, ensuring Industry Diversity
    // This ensures "If one is X, the others should not be X" (Maximum Diversity)
    uniqueDistinctRoles.forEach((role, index) => {
        // Find best available industry
        let industry = outputIndustries.find(ind => !usedIndustryNames.has(ind.name));
        // Fallback (rotate if we run out of unique industries)
        if (!industry) {
             industry = outputIndustries[index % outputIndustries.length];
        }
        usedIndustryNames.add(industry.name);
        // Set year to current year for first card options
        options.push({
            ...createOption(role.title, industry, role.type === 'Recommended' ? 'recommended' : 'pivot', role.template),
            year: currentYear
        });
    });

    return options;
};

/**
 * Generates distinct next-step options for the interactive builder.
 * Expanded to return 6-8 distinct options including Passion Pivots and Industry specializations.
 */
export const generateNextRoleOptions = (currentNode, userContext) => {
    const title = currentNode.title || "Specialist";
    
    // Helper to build a node
    // Include richer details (Industry, Salary range heuristic)
    const createOption = (newTitle, type, rationale, industry = null) => {
        // Heuristic Salary Generation
        const base = 90000;
        const multiplier = type === 'linear' ? 1.2 : (type === 'pivot' ? 1.1 : 1.0);
        const level = detectLevel(newTitle);
        const levelMult = 1 + (level * 0.2); // +20% per level
        
        const estSalary = Math.floor(base * multiplier * levelMult);
        const salaryRange = `$${Math.floor(estSalary/1000)}k - $${Math.floor((estSalary * 1.25)/1000)}k`;

        return {
            ...currentNode, 
            role_id: `${currentNode.role_id}_next_${Date.now()}_${Math.random()}`,
            title: newTitle,
            company: industry ? `${industry.name} Systems` : (currentNode.company || 'Tech Corp'),
            year: (parseInt(currentNode.year) || new Date().getFullYear()) + 1,
            description: rationale,
            salary: salaryRange,
            type: type,
            details: {
                skills: userContext?.skills || ['Leadership', 'Strategy', 'Technical Depth'],
                actions: ['Lead initiatives', 'Mentor juniors', 'Define scope']
            }
        };
    };

    const options = [];
    // Better base title extraction (Handle "Associate in AI" -> "AI")
    let baseTitle = title.replace(/Senior |Junior |Staff |Principal |Lead |Head of |Chief |VP of |Director of |Associate in |Associate /gi, "").trim();
    if (baseTitle === "AI") baseTitle = "Artificial Intelligence"; // Expansion

    // 1. --- LINEAR GROWTH (The "Obvious" Next Step) ---
    const nextLevelTitle = getNextTitle(title, 'linear');
    options.push(createOption(
        nextLevelTitle, 
        'linear', 
        `Step up to ${nextLevelTitle} with increased responsibility.`
    ));

    // 2. --- MANAGEMENT TRACK (The "Leader" Step) ---
    // Fix: "Associate in AI Manager" -> "AI Operations Manager"
    let managementTitle = getNextTitle(title, 'management');
    if (managementTitle.includes("Associate in AI Manager")) {
         managementTitle = "AI Project Manager";
    }
    
    options.push(createOption(
        managementTitle, 
        'management', // Fixed type
        `Transition into leadership as a ${managementTitle}.`
    ));

    // 3. --- SPECIALIST/FUNCTIONAL PIVOT ---
    let specializedPivot = "";
    // Advanced Heuristics for better pivots
    if (title.includes("Engineer")) specializedPivot = "Technical Product Manager";
    else if (title.includes("Manager")) specializedPivot = "Strategy Consultant";
    else if (title.includes("Designer")) specializedPivot = "Creative Director";
    else if (title.includes("Analyst")) specializedPivot = "Data Scientist";
    else if (title.toLowerCase().includes("ai") || title.toLowerCase().includes("data")) specializedPivot = "Machine Learning Engineer";
    else specializedPivot = `Principal ${baseTitle}`;
    
    options.push(createOption(
        specializedPivot, 
        'pivot', 
        `Pivot your functional focus towards ${specializedPivot}.`
    ));

    // 4. --- PASSION PIVOTS (Youtube/Hobby Driven) ---
    // Extract unique interest signals
    const signals = new Set();
    // From World Model
    if (userContext?.world_model?.interests) userContext.world_model.interests.forEach(i => signals.add(i));
    if (userContext?.world_model?.recent_topics) userContext.world_model.recent_topics.forEach(t => signals.add(t));
    if (userContext?.world_model?.skills) userContext.world_model.skills.forEach(t => signals.add(t));
    
    // From YouTube
    const ytData = userContext?.resume_data?.youtube;
    if (ytData) {
        if (ytData.interests) ytData.interests.forEach(i => signals.add(i));
        if (ytData.recurring_topics) ytData.recurring_topics.forEach(t => signals.add(t));
        if (ytData.hobbies) ytData.hobbies.forEach(h => signals.add(h));
    }
    
    // Pick 2-3 distinct passions if available
    const passions = Array.from(signals).filter(s => s && s.length < 20 && !title.toLowerCase().includes(s.toLowerCase()));
    // Shuffle passions
    passions.sort(() => Math.random() - 0.5);
    
    passions.slice(0, 3).forEach(passion => {
        // Construct "Passion Role"
        // e.g. "Gaming" + "Software Engineer" -> "Gameplay Programmer"
        let passionTitle = `${passion} ${baseTitle}`;
        let passionDesc = `Combine your professional skills with your interest in ${passion}.`;
        
        // Simple heuristic refinement for nice titles
        if (passion.toLowerCase().includes('game') || passion.toLowerCase().includes('gaming')) {
             passionTitle = `Gameplay ${baseTitle}`;
        }
        
        options.push(createOption(
            passionTitle, 
            'passion', 
            passionDesc
        ));
    });

    // 5. --- INDUSTRY PIVOTS (Context Shifting) ---
    // Pick 2 random industries distinct from current role if possible
    const shuffledIndustries = [...industries].sort(() => Math.random() - 0.5);
    let industriesAdded = 0;
    
    for (const ind of shuffledIndustries) {
        if (industriesAdded >= 2) break;
        // Check if title already includes this industry name (avoid redundancy)
        if (!title.toLowerCase().includes(ind.name.toLowerCase())) {
             options.push(createOption(
                `${ind.name} ${baseTitle}`, 
                'industry', 
                `Apply your expertise in the high-growth ${ind.name} sector: ${ind.desc.toLowerCase()}.`,
                ind // Pass industry object to helper
             ));
             industriesAdded++;
        }
    }
    
    // 6. --- SHUFFLE & RETURN ---
    // Keep "Linear" (First option) at the front for anchoring, shuffle the rest
    if (options.length > 0) {
        const [first, ...rest] = options;
        rest.sort(() => Math.random() - 0.5);
        return [first, ...rest].slice(0, 8); // Return up to 8 options
    }
    
    return options; // Should not happen
};


// --- PATH GENERATION HELPERS ---

/**
 * Returns a deterministic but distinct sequence for "Adventurous" paths.
 * Strategy: Adjacent pivots, broader scope, maintaining stability but changing domain.
 */
function getAdventurousSequence(baseTitle) {
    const title = baseTitle.toLowerCase();
    
    // Tech -> Product/People
    if (title.includes('engineer') || title.includes('developer')) {
        return [
            { title: "Technical Product Manager", desc: "Leveraging technical depth to drive product strategy." },
            { title: "Director of Product", desc: "Leading product portfolio and vision." },
            { title: "VP of Product Innovation", desc: "Defining the future of the company's product suite." }
        ];
    }
    
    // Product -> Growth/Strategy
    if (title.includes('product')) {
        return [
            { title: "Head of Growth", desc: "Shifting focus to acquisition and user scaling." },
            { title: "VP of Growth", desc: "Owning the entire growth engine and revenue." },
            { title: "Chief Revenue Officer", desc: "Bridging product value with commercial success." }
        ];
    }
    
    // Design -> Creative/Brand
    if (title.includes('design') || title.includes('creative')) {
        return [
            { title: "Creative Strategist", desc: "Applying design thinking to business challenges." },
            { title: "Head of Brand Experience", desc: "Orchestrating the complete user journey and brand." },
            { title: "Chief Experience Officer", desc: "Ensuring holistic quality across all touchpoints." }
        ];
    }

    // Marketing -> Strategy/Ops
    if (title.includes('marketing') || title.includes('brand')) {
        return [
            { title: "Product Marketing Lead", desc: "Getting closer to the product definition and launch." },
            { title: "Director of Strategy", desc: "Guiding long-term market positioning." },
            { title: "CMO (Scale-up)", desc: "Leading marketing for a high-growth challenger." }
        ];
    }

    // Default -> Strategy Pivot
    return [
        { title: `Strategic ${baseTitle.split(' ').pop()}`, desc: "Moving from execution to high-level strategy." },
        { title: "Director of Operations", desc: "Optimizing the business machine." },
        { title: "VP of Strategy", desc: "Steering the company's long-term direction." }
    ];
}

/**
 * Returns a deterministic but distinct sequence for "Risky" paths.
 * Strategy: Asymmetric bets, high variance, equity focus, founding/investing.
 */
function getRiskySequence(baseTitle) {
    const title = baseTitle.toLowerCase();
    
    // Tech -> Founder/Startups
    if (title.includes('engineer') || title.includes('developer') || title.includes('data') || title.includes('product')) {
        return [
            { title: "Co-Founder (Stealth Startup)", desc: "Building something from 0 to 1. High equity, high risk." },
            { title: "CTO / CPO (Series A)", desc: "Scaling a proven product in a chaotic environment." },
            { title: "Angel Investor / Scout", desc: "Investing capital and reputation in next-gen founders." }
        ];
    }
    
    // Creative/Marketing -> Solo/Agency
    if (title.includes('design') || title.includes('marketing') || title.includes('writer')) {
        return [
            { title: "Independent Creator / Consultant", desc: "Betting on your personal brand and direct monetization." },
            { title: "Founder (Boutique Agency)", desc: "Building a specialized firm with your unique signature." },
            { title: "Media Proprietor", desc: "Owning the platform and distribution entirely." }
        ];
    }

    // Default -> Founder Path
    return [
        { title: "Founding Member (Seed Stage)", desc: "First 10 employee. Wearing every hat possible." },
        { title: "Co-Founder (New Venture)", desc: "Taking the helm to solve a problem you discovered." },
        { title: "Venture Partner", desc: "Using operator experience to spot winners." }
    ];
}

/**
 * Generates an "Adventurous" path.
 * Strategic pivots, high-growth industries, utilizing transferable skills.
 */
export const generateAdventurousPath = (userNodes) => {
    const baseNode = userNodes[0];
    const currentYear = new Date().getFullYear();

    // Start with base node but ensure it uses current year
    const startNode = {
        ...baseNode,
        year: currentYear
    };
    const nodes = [startNode];

    // Get sequence
    const sequence = getAdventurousSequence(baseNode.title);

    // 1. Strategic Pivot (Year +2)
    const step1 = {
        ...baseNode,
        role_id: 'adv_1',
        title: sequence[0].title,
        year: currentYear + 2,
        description: sequence[0].desc,
        salary_range: { min: 110000, max: 150000, currency: 'USD' }
    };
    nodes.push(step1);

    // 2. Leadership in New Domain (Year +5)
    const step2 = {
        ...step1,
        role_id: 'adv_2',
        title: sequence[1].title,
        year: currentYear + 5,
        description: sequence[1].desc,
        salary_range: { min: 160000, max: 220000, currency: 'USD' }
    };
    nodes.push(step2);

    // 3. High-Level Impact (Year +8)
    const step3 = {
        ...step2,
        role_id: 'adv_3',
        title: sequence[2].title,
        year: currentYear + 8,
        description: sequence[2].desc,
        salary_range: { min: 250000, max: 350000, currency: 'USD' }
    };
    nodes.push(step3);

    return {
        id: 'adventurous-path',
        nodes: nodes
    };
};

/**
 * Generates a "Risky" path.
 * Moonshots, founding companies, or radical career pivots.
 */
export const generateRiskyPath = (userNodes) => {
    const baseNode = userNodes[0];
    const currentYear = new Date().getFullYear();

    // Start with base node but ensure it uses current year
    const startNode = {
        ...baseNode,
        year: currentYear
    };
    const nodes = [startNode];

    // Get sequence
    const sequence = getRiskySequence(baseNode.title);
    
    // OVERLAP CHECK: 
    // If by chance the Adventurous path generated a "Founder" role (unlikely with current logic but possible if extended),
    // we would handle it here. 
    // Since we hardcoded disjoint sets (Manager/Director/VP vs Founder/CXO/Investor), we are safe.
    // However, let's explicitly ensure Step 1 is NOT generic.

    // 1. Founding / Early Stage (Year +1)
    const jump1 = {
        ...baseNode,
        role_id: 'risk_1',
        title: sequence[0].title,
        year: currentYear + 1,
        description: sequence[0].desc,
        salary_range: { min: 0, max: 80000, currency: 'USD', note: "High Equity" }
    };
    nodes.push(jump1);

    // 2. Growth / Scale (Year +4)
    const jump2 = {
        ...jump1,
        role_id: 'risk_2',
        title: sequence[1].title,
        year: currentYear + 4,
        description: sequence[1].desc,
        salary_range: { min: 180000, max: 250000, currency: 'USD' }
    };
    nodes.push(jump2);

    // 3. Exit / VC (Year +8)
    const jump3 = {
        ...jump2,
        role_id: 'risk_3',
        title: sequence[2].title,
        year: currentYear + 8,
        description: sequence[2].desc,
        salary_range: { min: 500000, max: 1000000, currency: 'USD' }
    };
    nodes.push(jump3);

    return {
        id: 'risky-path',
        nodes: nodes
    };
};


// --- Helpers ---

const levels = ['Junior', 'Mid', 'Senior', 'Staff', 'Principal', 'Distinguished'];
const mgmtLevels = ['Lead', 'Manager', 'Head', 'Director', 'VP', 'C-Level'];

function detectLevel(title) {
    if (/junior|associate|intern/i.test(title)) return 0;
    if (/senior|sr\./i.test(title)) return 2;
    if (/staff/i.test(title)) return 3;
    if (/principal/i.test(title)) return 4;
    return 1; // Mid
}

function getNextTitle(currentTitle, type) {
    // Simple heuristic title bumper
    const clean = currentTitle.replace(/junior|senior|staff|principal|lead|head of|vp|chief/gi, '').trim();
    
    if (type === 'linear') {
        if (currentTitle.includes('Junior')) return clean;
        if (!currentTitle.includes('Senior')) return `Senior ${clean}`;
        if (currentTitle.includes('Senior')) return `Staff ${clean}`;
        return `Principal ${clean}`;
    }
    
    if (type === 'management') {
         if (currentTitle.includes('Manager')) return `Senior Manager ${clean}`;
         if (currentTitle.includes('Head')) return `VP of ${clean}`;
         return `${clean} Manager`; // Default first mgmt step
    }
    
    if (type === 'specialist') {
        return `Principal ${clean}`;
    }
    
    return `Senior ${clean}`;
}
